/**
 * Redux thunk action creators for a project's rule evaluation jobs.
 * Backed by the real jobs API. See `facades/jobsApi.ts`.
 *
 * Every thunk carries `projectId` through to its fulfilled action, because the
 * jobs slice is keyed by project and the reducer has no other way to know
 * which entry an action belongs to.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as JobsApi from '../facades/jobsApi';
import { ApiErrorPayload, toApiErrorPayload } from '../facades/apiFacade';
import { RuleEvaluationJob, RuleEvaluationResult } from '../../@types/jobs';
import type { ThunkAPI } from '../stores/store';

type JobsThunkAPI = ThunkAPI & { rejectValue: ApiErrorPayload & { projectId: string } };

/** Attach the project so the reducer can route a rejection to the right entry. */
function reject(error: unknown, projectId: string) {
    return { ...toApiErrorPayload(error), projectId };
}

export const fetchJobs = createAsyncThunk<
    { projectId: string; jobs: RuleEvaluationJob[] },
    { accessToken: string; projectId: string },
    JobsThunkAPI
>('jobs/fetchJobs', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, jobs: await JobsApi.getJobs(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

/**
 * Refreshes one job by id, for polling the single job a project can have
 * active rather than re-listing every job on an interval.
 */
export const fetchJob = createAsyncThunk<
    { projectId: string; job: RuleEvaluationJob },
    { accessToken: string; projectId: string; jobId: string },
    JobsThunkAPI
>('jobs/fetchJob', async ({ accessToken, projectId, jobId }, thunkApi) => {
    try {
        return { projectId, job: await JobsApi.getJob(accessToken, projectId, jobId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const triggerJob = createAsyncThunk<
    { projectId: string; job: RuleEvaluationJob },
    { accessToken: string; projectId: string },
    JobsThunkAPI
>('jobs/triggerJob', async ({ accessToken, projectId }, thunkApi) => {
    try {
        return { projectId, job: await JobsApi.triggerJob(accessToken, projectId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});

export const fetchJobResults = createAsyncThunk<
    { projectId: string; jobId: string; results: RuleEvaluationResult[] },
    { accessToken: string; projectId: string; jobId: string },
    JobsThunkAPI
>('jobs/fetchJobResults', async ({ accessToken, projectId, jobId }, thunkApi) => {
    try {
        return { projectId, jobId, results: await JobsApi.getJobResults(accessToken, projectId, jobId) };
    } catch (error) {
        return thunkApi.rejectWithValue(reject(error, projectId));
    }
});
